import React from "react";
import { shallowEqual } from "react-redux";
import { useTranslation } from "react-i18next";
import polyline from "@mapbox/polyline";
import { CircleMarker, Polyline, Popup, useMap } from "react-leaflet";
import { LatLngBoundsExpression, Map } from "leaflet";

import { useAppSelector } from "../../redux/hooks";
import { InfoPopup } from "../helpers/PolylinesComponents";
import { zoomRange } from "../../config";
import { getWheelColors } from "../../utils/helperWheelColors";
import { stopsOptionsConstant } from "../../data/constants";

const TripInfoMap = (): JSX.Element => {
  const leafletMap: Map = useMap();

  const [tripInfoData]: [TripInfoReport | null] = useAppSelector(
    (state: RootState) => [state?.constantValues?.tripInfoData],
    shallowEqual
  );
  // console.log("tripInfoData:", tripInfoData);

  React.useEffect(() => {
    if (tripInfoData && Object.keys(tripInfoData)?.length && leafletMap) {
      const { schedule } = tripInfoData;
      // console.log("schedule:", schedule);
      const positions: LanLngPoint[] = schedule?.map((elem: Schedule) => elem?.position);
      // console.log("positions:", positions);
      const lanArray: number[] = positions?.map((elem: LanLngPoint) => elem?.lat);
      const lonArray = positions?.map((elem: LanLngPoint) => elem?.lon) as number[];

      const [minLan, maxLan]: [number, number] = [Math.min(...lanArray), Math.max(...lanArray)];
      const [minLon, maxLon]: [number, number] = [Math.min(...lonArray), Math.max(...lonArray)];

      const bounds: [number, number][] = [
        [minLan - zoomRange, minLon - zoomRange],
        [maxLan + zoomRange, maxLon + zoomRange],
      ];
      leafletMap.fitBounds(bounds as LatLngBoundsExpression);
    }
  }, [tripInfoData, leafletMap]);

  return (
    <React.Fragment>
      {tripInfoData ? (
        <React.Fragment>
          <TripPolyline
            encodedTripPolyline={tripInfoData?.encodedTripPolyline}
            routeColor={tripInfoData?.routeColor}
            tripId={tripInfoData?.tripId}
          />

          <TripStops schedule={tripInfoData?.schedule} color={tripInfoData?.routeColor} />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export default TripInfoMap;

//* TripPolyline
const TripPolyline = ({
  encodedTripPolyline,
  routeColor,
  tripId,
}: {
  encodedTripPolyline: string;
  routeColor: string;
  tripId: string;
}): JSX.Element => {
  const { t } = useTranslation();

  const positions: [number, number][] = polyline.decode(encodedTripPolyline, 6);
  const pathOptions = { opacity: 1, color: routeColor, weight: 4 };

  return (
    <React.Fragment>
      <Polyline
        pathOptions={pathOptions}
        positions={positions}
        children={
          <Popup>
            <InfoPopup $width={"auto"}>
              <p>
                {t("Kurs")}: <span>{tripId || t("b/d")}</span>
              </p>
            </InfoPopup>
          </Popup>
        }
      />
    </React.Fragment>
  );
};

const TripStops = ({ schedule, color }: { schedule: Schedule[]; color: string }): JSX.Element => {
  const { t } = useTranslation();

  const wheelColors: string[] = getWheelColors(color, 2);
  const stopsOptions = {
    ...stopsOptionsConstant,
    fillColor: wheelColors?.[0],
    color: wheelColors?.[1],
  };

  return (
    <React.Fragment>
      {
        schedule?.map((elem: Schedule, index: number) => (
          <CircleMarker
            pathOptions={stopsOptions}
            key={String(elem?.position?.lat + " " + elem?.position?.lon + " " + index)}
            center={{
              lat: elem?.position?.lat,
              lng: elem?.position?.lon as number,
            }}
            radius={6}
          >
            <Popup>
              <div>
                {t("Nazwa")} <span className="span_bold">{elem?.stopName}</span>
                <br />
                {t("Id")} <span className="span_bold">{elem?.stopId}</span>
                <br />
                <small className="text-muted">
                  {t("Pozycja")}:{" "}
                  <span className="span_bold text-muted">{`${elem?.position?.lat}; ${elem?.position?.lon}`}</span>
                </small>
              </div>
            </Popup>
          </CircleMarker>
        )) as JSX.Element[]
      }
    </React.Fragment>
  );
};
